// Per-route error boundary wrapper. Wraps a route element so a render
// error inside one page never blanks the whole app. Shows a friendly
// fallback with a reload + home link; the actual error is shipped to
// the admin queue by the inner ErrorBoundary.
import { type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { RefreshCw, Home } from "lucide-react";
import ErrorBoundary from "@/components/ErrorBoundary";

const Fallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center px-6">
    <div className="max-w-md text-center">
      <p className="text-[10px] font-mono uppercase tracking-wider text-primary mb-2">
        $ recover --page
      </p>
      <h1 className="font-mono font-extrabold text-2xl text-foreground mb-2">
        This page hit a snag
      </h1>
      <p className="text-sm text-muted-foreground font-mono mb-6">
        The rest of the app is fine. The team's been notified — try a reload, or
        head back home.
      </p>
      <div className="flex items-center justify-center gap-2">
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary text-primary-foreground font-mono text-sm font-semibold hover:bg-primary/90 transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Reload
        </button>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-border text-foreground font-mono text-sm hover:border-primary/40 transition-colors"
        >
          <Home className="w-3.5 h-3.5" /> Home
        </Link>
      </div>
    </div>
  </div>
);

// Reset the boundary on every route change. Otherwise an error stays
// stuck even when the user navigates away — they'd need a hard reload.
// Keying ErrorBoundary on pathname remounts it (fresh state) per route.
const RouteBoundary = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  return (
    <ErrorBoundary key={location.pathname} fallback={<Fallback />}>
      {children}
    </ErrorBoundary>
  );
};

export default RouteBoundary;